import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Overlay,
  Dialog,
  Header,
  Title,
  Desc,
  Actions,
  Button,
  ApiError,
  ConfirmLabel,
  ConfirmInput,
  PhraseHint,
} from "./styles";
import { useTaskStore } from "store/task";
import { storageAPI } from "api";

const REQUIRED_PHRASE = "삭제하겠습니다";

const DeleteVolume = ({ open, setOpen, base }) => {
  const { addTask } = useTaskStore();

  const [name, setName] = useState("");
  const [phrase, setPhrase] = useState("");
  const [apiErr, setApiErr] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setName(base);
    setPhrase("");
    setApiErr(null);
    const t = setTimeout(() => nameRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open, base]);

  // ✅ boolean만 반환하도록 정리 (TS 경고 제거)
  const canSubmit = useMemo(
    () => Boolean(name.trim()) && phrase.trim() === REQUIRED_PHRASE,
    [name, phrase]
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setApiErr(null);
      const res = await storageAPI.deleteVolumes(name.trim());
      if (res?.data?.task_id) {
        addTask({
          name,
          id: res.data.task_id,
          job: "Delete Volume",
          state: "pending",
        });
      }
      setOpen(false);
    } catch (err: any) {
      setApiErr(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to delete volume."
      );
    }
  };

  if (!open) return null;

  // ✅ 여기서 body로 포털 렌더링
  return createPortal(
    <Overlay>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-volume-title"
        tabIndex={-1}
      >
        <Header>
          <Title id="delete-volume-title">Delete Volume</Title>
        </Header>

        <Desc>볼륨을 삭제합니다. 이 작업은 되돌릴 수 없습니다.</Desc>

        {apiErr && <ApiError role="alert">{apiErr}</ApiError>}

        <form
          onSubmit={onSubmit}
          style={{ display: "grid", gap: 12, marginTop: 8 }}
        >
          <ConfirmLabel>Volume name</ConfirmLabel>
          <ConfirmInput
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex) oasys_test01"
            inputMode="text"
          />

          <ConfirmLabel>
            다음 문구를 그대로 입력하세요 -{" "}
            <code style={{ marginLeft: 4 }}>{REQUIRED_PHRASE}</code>
          </ConfirmLabel>
          <ConfirmInput
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={REQUIRED_PHRASE}
            inputMode="text"
            data-invalid={
              phrase.length > 0 && phrase.trim() !== REQUIRED_PHRASE
            }
          />
          <PhraseHint
            data-visible={
              phrase.length > 0 && phrase.trim() !== REQUIRED_PHRASE
            }
          >
            {REQUIRED_PHRASE} 라고 정확히 입력해야 합니다.
          </PhraseHint>

          <Actions>
            <Button
              type="button"
              onClick={() => setOpen(false)}
              data-variant="ghost"
            >
              Cancel
            </Button>
            <Button type="submit" data-variant="danger" disabled={!canSubmit}>
              Delete
            </Button>
          </Actions>
        </form>
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default DeleteVolume;
